import axios from "axios";
import { API_BASE_URL } from "./constants";
import i18n from "../i18n";

/** Shared axios client with JWT attach + refresh (TD-05 / FE-03). */
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Single-flight refresh within this tab.
 * With ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION, parallel refreshes
 * with the same refresh token would log the user out.
 */
let refreshPromise = null;

function clearSessionAndRedirect() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    throw new Error(i18n.t("apiErrors.refreshMissing"));
  }

  try {
    const refreshResponse = await axios.post(`${API_BASE_URL}/token/refresh/`, {
      refresh: refreshToken,
    });
    const newAccessToken = refreshResponse.data.access;
    if (!newAccessToken) {
      throw new Error("Access token missing in refresh response");
    }
    if (refreshResponse.data.refresh) {
      localStorage.setItem("refresh_token", refreshResponse.data.refresh);
    }
    localStorage.setItem("access_token", newAccessToken);
    api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
    return newAccessToken;
  } catch (err) {
    // Another tab may have rotated already — pick up tokens it wrote.
    await new Promise((resolve) => setTimeout(resolve, 75));
    const latestRefresh = localStorage.getItem("refresh_token");
    const latestAccess = localStorage.getItem("access_token");
    if (latestAccess && latestRefresh && latestRefresh !== refreshToken) {
      api.defaults.headers.Authorization = `Bearer ${latestAccess}`;
      return latestAccess;
    }
    throw err;
  }
}

function getSharedRefreshPromise() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (originalRequest.url?.includes("/token/refresh/")) {
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newAccessToken = await getSharedRefreshPromise();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);
